import React, { useEffect, useMemo, useState } from 'react';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { apiService } from '../services/api';
import { useEscapeToClose } from '../hooks/useEscapeToClose';
import RelativeTime from '../components/common/RelativeTime';
import '../components/Auth/RegisterModal.css';
import './ManageCompetitionModals.css';

interface PairPoolRow {
  id: number;
  competition_id: number;
  user_a_id: number;
  user_b_id: number;
  user_a: string;
  user_b: string;
  times_compared: number;
  last_compared_at?: string | null;
  active: boolean;
  exhausted: boolean;
  created_at: string;
}

interface AdminPairPoolModalProps {
  isOpen: boolean;
  onClose: () => void;
  competitionId: number;
}

const AdminPairPoolModal: React.FC<AdminPairPoolModalProps> = ({ isOpen, onClose, competitionId }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [rows, setRows] = useState<PairPoolRow[]>([]);

  useEffect(() => {
    if (!isOpen) return;
    setLoading(false);
    setError('');
    setQuery('');
    setRows([]);

    const run = async () => {
      setLoading(true);
      try {
        const res = await apiService.get(`/evaluations/pairpool/${competitionId}`, {
          params: { limit: 1000, offset: 0 },
        });
        setRows(res.data || []);
      } catch (err: any) {
        console.error('Failed to load PairPool', err);
        const msg = err?.response?.data?.detail || err?.message || 'Failed to load PairPool';
        setError(String(msg));
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [competitionId, isOpen]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const hay = `${r.user_a} ${r.user_b} ${r.times_compared} ${r.active ? 'active' : ''} ${r.exhausted ? 'exhausted' : ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [query, rows]);

  useEscapeToClose(onClose, { enabled: isOpen });

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className="register-modal-backdrop" onClick={handleBackdropClick}>
      <div
        className="register-modal"
        role="dialog"
        aria-modal="true"
        style={{ width: 'min(1100px, 96vw)', maxWidth: '1100px' }}
      >
        <div className="register-modal__header">
          <h2 className="register-modal__title">PairPool</h2>
          <button type="button" className="register-modal__close" onClick={onClose} aria-label="Close modal">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="register-modal__form">
          {error && (
            <div className="register-modal__error" role="alert">
              {error}
            </div>
          )}

          {loading && <LoadingSpinner text="Loading PairPool..." />}

          {!loading && (
            <>
              <div className="register-modal__fields">
                <Input
                  name="query"
                  label="Search"
                  placeholder="Filter by username"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />

                <div className="mcm-text-secondary" style={{ fontSize: '0.9rem' }}>
                  Showing <strong>{filtered.length}</strong> pairs
                </div>

                <div
                  className="mcm-table-wrap"
                  style={{
                    maxHeight: 420,
                    overflow: 'auto',
                    borderRadius: 10,
                  }}
                >
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr className="mcm-table-head" style={{ textAlign: 'left' }}>
                        <th style={{ padding: '0.65rem' }}>A</th>
                        <th style={{ padding: '0.65rem' }}>B</th>
                        <th style={{ padding: '0.65rem' }}>Times Compared</th>
                        <th style={{ padding: '0.65rem' }}>Active</th>
                        <th style={{ padding: '0.65rem' }}>Exhausted</th>
                        <th style={{ padding: '0.65rem' }}>Last Compared</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((r) => {
                        return (
                          <tr key={r.id} className="mcm-row">
                            <td style={{ padding: '0.65rem' }}>{r.user_a}</td>
                            <td style={{ padding: '0.65rem' }}>{r.user_b}</td>
                            <td style={{ padding: '0.65rem' }}>{r.times_compared}</td>
                            <td style={{ padding: '0.65rem' }}>{r.active ? 'yes' : 'no'}</td>
                            <td style={{ padding: '0.65rem' }}>{r.exhausted ? 'yes' : 'no'}</td>
                            <td style={{ padding: '0.65rem', whiteSpace: 'nowrap' }}><RelativeTime value={r.last_compared_at} /></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="register-modal__actions">
                <Button type="button" variant="secondary" onClick={onClose}>
                  Close
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPairPoolModal;
